import React, { useEffect, useState, useCallback } from "react";
import "./subscription.css";
import { useNavigate } from "react-router-dom";

import { Navbar, FloatingAlert, Footer } from "../../components";
import {
  SelectSubscriptionSection,
  PixSubscriptionSection,
  CryptoSubscriptionSection,
  PaymentPendingSection,
} from "../../sections";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { ClipLoader } from "react-spinners";
import usePaymentStatus from "../../hooks/usePaymentStatus";

function Subscription() {
  const [page, setPage] = useState("select-section");
  const [showPlanNotSelectedWarning, setShowPlanNotSelectedWarning] =
    useState(false);
  const [selectedPlan, setSelectedPlan] = useState({});
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [paymentConfirmed, setPaymentConfirmed] = useState(false);
  const [showPaymentAlert, setShowPaymentAlert] = useState(false);
  const [paymentInitiated, setPaymentInitiated] = useState(false);
  const [checkingExistingPayment, setCheckingExistingPayment] = useState(true);
  const [recentFailedPayment, setRecentFailedPayment] = useState(false);
  const [failedPaymentDate, setFailedPaymentDate] = useState(null);

  const SECTIONS_TITLES = {
    "select-section": "Finalize sua assinatura",
    "pix-section": "Pagamento com PIX",
    "crypto-section": "Pagamento com Criptomoeda",
    "payment-pending": "Status do Pagamento",
  };

  const SECTIONS_SUBTITLES = {
    "select-section":
      "ASSINATURA ANUAL — ACEITAMOS PAGAMENTOS VIA PIX E CRIPTOMOEDA",
  };

  const navigate = useNavigate();

  const { paymentData, fetchPaymentStatus } = usePaymentStatus({
    polling: true,
  });

  const checkInitialPaymentStatus = useCallback(async () => {
    try {
      const paymentStatus = await fetchPaymentStatus();
      if (paymentStatus?.payment?.status === "pending") {
        setPaymentInitiated(true);
        setPage("payment-pending");
        return;
      }
      if (paymentStatus?.payment?.status === "failed") {
        const failureDate = new Date(paymentStatus.payment.updated_at);
        const oneWeekAgo = new Date();
        oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

        if (failureDate > oneWeekAgo) {
          setRecentFailedPayment(true);
          setFailedPaymentDate(failureDate);
        }
        setPage("select-section");
      }
    } catch (error) {
      setPaymentInitiated(false);
      setPage("select-section");
      setCheckingExistingPayment(false);
    }
  }, [fetchPaymentStatus]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setShowPlanNotSelectedWarning(false);
    setRecentFailedPayment(false);
    setFailedPaymentDate(null);
    if (newPage === "select-section") {
      setPaymentInitiated(false);
    }
  };

  const pageComponents = {
    "select-section": (
      <SelectSubscriptionSection
        selectedPlan={selectedPlan}
        setSelectedPlan={setSelectedPlan}
        selectedPaymentMethod={selectedPaymentMethod}
        setSelectedPaymentMethod={setSelectedPaymentMethod}
        setShowPlanNotSelectedWarning={setShowPlanNotSelectedWarning}
        page={page}
        setPage={handlePageChange}
      />
    ),
    "pix-section": (
      <PixSubscriptionSection
        selectedPlan={selectedPlan}
        page={page}
        setPage={handlePageChange}
        setSelectedPlan={setSelectedPlan}
        setPaymentConfirmed={setPaymentConfirmed}
        setPaymentInitiated={setPaymentInitiated}
      />
    ),
    "crypto-section": (
      <CryptoSubscriptionSection
        selectedPlan={selectedPlan}
        page={page}
        setPage={handlePageChange}
        setPaymentInitiated={setPaymentInitiated}
        fetchPaymentStatus={fetchPaymentStatus}
      />
    ),
    "payment-pending": (
      <PaymentPendingSection
        selectedPlan={selectedPlan}
        paymentMethod={selectedPaymentMethod}
        setPage={handlePageChange}
        paymentData={paymentData}
      />
    ),
  };

  useEffect(() => {
    const initializePage = async () => {
      setCheckingExistingPayment(true);
      await checkInitialPaymentStatus();
      setCheckingExistingPayment(false);
    };

    initializePage();
  }, [checkInitialPaymentStatus]);

  useEffect(() => {
    if (paymentInitiated && !paymentConfirmed) {
      setPage("payment-pending");
    }
  }, [paymentInitiated, paymentConfirmed]);

  useEffect(() => {
    paymentConfirmed ? setShowPaymentAlert(true) : setShowPaymentAlert(false);

    if (showPaymentAlert) {
      setTimeout(function () {
        setShowPaymentAlert(false);
        navigate("/dashboard");
      }, 2000);
    }
  }, [paymentConfirmed, showPaymentAlert]);

  return (
    <div className="orion__page">
      <Navbar />
      <div className="orion__subscription">
        <div className="orion__subscription-container">
          <div className="orion__container-header">
            <div className="orion__header-content">
              <h1 className="orion__title">
                {SECTIONS_TITLES[page] || "Finalize sua assinatura"}
              </h1>
              <small>{SECTIONS_SUBTITLES[page] || ""}</small>
            </div>
          </div>
          {recentFailedPayment && (
            <div className="orion__payment-failed-warning">
              <ExclamationCircleIcon className="orion__warning-icon" />
              <small>
                Vimos que seu pagamento do dia{" "}
                {failedPaymentDate?.toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                })}{" "}
                falhou, tente novamente
              </small>
            </div>
          )}
          <div className="orion__subscription-section">
            {checkingExistingPayment ? (
              <div className="orion__loading-container">
                <ClipLoader color="#D42A5B" size={50} />
                <p>Verificando pagamentos pendentes...</p>
              </div>
            ) : (
              pageComponents[page]
            )}
          </div>
          {showPlanNotSelectedWarning ? (
            <div className="orion__plan-not-selected-warning">
              <ExclamationCircleIcon className="orion__warning-icon" />
              <small>
                Selecione uma forma de pagamento para continuar
              </small>
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      {showPaymentAlert && <FloatingAlert text="Pagamento confirmado!" />}
      {recentFailedPayment && (
        <div className="orion__support-contact orion__support-contact--visible">
          <span className="orion__support-icon" aria-hidden="true">
            💬
          </span>
          <span>
            Precisa de ajuda? Entre em contato:{" "}
            <a
              href="mailto:suporte@orionsinais.com.br"
              className="orion__support-link"
            >
              suporte@orionsinais.com.br
            </a>
          </span>
        </div>
      )}
      <Footer />
    </div>
  );
}

export default Subscription;
